import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useAllTags } from 'app/network/listing';
import type { Tag } from 'app/network/listing';
import React from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
import { moveOtherTagToEnd } from 'app/screens/billboard/all-listings/sort-tags';

const theme = createDSTheme();

interface LandingCategoriesProps {
  onTagPress: (tagId: Tag['id']) => void;
  onViewAll: () => void;
  variant: 'small' | 'large';
}

export function LandingCategories({ onTagPress, onViewAll, variant }: LandingCategoriesProps) {
  // Haute-Gaspésie treats the published tags as its browse interests. Keep the
  // same popularity ordering as the offer catalog header.
  const { data: interests } = useAllTags(true, { select: moveOtherTagToEnd });
  const { t } = useTranslation('billboard');
  const { t: tListings } = useTranslation('all-listings');
  const isLarge = variant === 'large';

  if (!interests?.length) return null;

  return (
    <Column gap="spacing.general.x4" testID="landing-categories">
      <Row
        alignItems="center"
        justifyContent="space-between"
        gap="spacing.general.x4"
        style={isLarge ? styles.largeHorizontalPadding : undefined}
      >
        <Text color="text.primary" fontSize={20} fontWeight="700" lineHeight={24}>
          {t('browseByCategories')}
        </Text>

        {isLarge ? (
          <Pressable accessibilityRole="button" onPress={onViewAll}>
            <Text color="text.interactive" fontSize={14} fontWeight="700">
              {t('viewAllCatalogButton')}
            </Text>
          </Pressable>
        ) : null}
      </Row>

      <FlatList
        contentContainerStyle={[styles.categories, isLarge ? styles.largeCategories : undefined]}
        data={interests}
        horizontal
        keyExtractor={(interest) => interest.id}
        ListHeaderComponent={<CategoryPill label={tListings('filters.all')} onPress={onViewAll} />}
        renderItem={({ item: interest }) => (
          <CategoryPill label={interest.tagName} onPress={() => onTagPress(interest.id)} />
        )}
        showsHorizontalScrollIndicator={false}
      />
    </Column>
  );
}

function CategoryPill({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [styles.pill, pressed && styles.pressed]}
    >
      <Text color="text.primary" fontSize={14} fontWeight="400" numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  categories: {
    gap: theme.spacing['spacing.general.x2'],
    paddingRight: theme.spacing['spacing.general.x4'],
  },
  largeCategories: {
    paddingLeft: theme.spacing['spacing.general.x4'],
  },
  largeHorizontalPadding: {
    paddingHorizontal: theme.spacing['spacing.general.x4'],
  },
  pill: {
    alignItems: 'center',
    backgroundColor: theme.colors['surface.default'],
    borderColor: theme.colors['border.secondary'],
    borderRadius: 24,
    borderWidth: 1,
    height: 48,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['spacing.general.x4'],
  },
  pressed: {
    opacity: 0.65,
  },
});
